<script>
import useSharedData from "@/use/SharedData";
import useSharedMethod from "@/use/SharedMethod";
import useTools from "@/use/Tools";
import useValidation from "@/use/Validation";
import StrCvEmailImport from "@/views/Recruitment/Sourcing/Components/StrCvEmailImport.vue";
import StrCvTechImport from "@/views/Recruitment/Sourcing/Components/StrCvTechImport.vue";
import StrCvFolderImport from "@/views/Recruitment/Sourcing/Components/StrCvFolderImport.vue";

import {computed, onMounted, onUnmounted, ref, watch} from "vue";
import axios from "axios";
import useSharedSelectData from "@/use/SharedSelectData";
import DejaVue from "@/views/Recruitment/Sourcing/Components/DejaVu.vue";
import {useSecurityStore} from "@/stores/Security";
import {storeToRefs} from "pinia";
import {useRecruitmentCvTechSourcingStore} from "@/stores/Recruitment/RecruitmentCvTechSourcing";
import {useDownload} from "@/use/Download";

export default {
    components: {DejaVue, StrCvFolderImport, StrCvTechImport, StrCvEmailImport},
    props: {
        addAction: Boolean,
        updateAction: Boolean,
        deleteAction: Boolean,
        viewAction: Boolean,
    },
    setup(props) {
        const readonly = computed(() => props.deleteAction || props.viewAction);
        const {dialog, loading, inputColor, backgroundColor, chipElevation} = useSharedData();
        const secondDialog = ref(false);
        const {cvTechSourcingStates} = useSharedSelectData();
        const {getBack, transformFilterValues} = useSharedMethod();
        const {downloadOrOpen, openLoading, downloadLoading} = useDownload();
        const {$route, $router, $refs} = useTools();
        const {mxRules} = useValidation();
        
        const recruitmentCvTechSourcingStore = useRecruitmentCvTechSourcingStore();
        const securityStore = useSecurityStore();
        const {refresh} = storeToRefs(securityStore)
        
        const stepper = ref(1);
        const panelModel = ref(0);
        const selectOption = ref("WAITING");
        const sourcing = ref({
            closed: true,
            cvTechs: []
        });
        
        const cvCache = ref([]);
        const lastPageUploaded = ref(0);
        
        function downloadAndCacheCv() {
            let cvTech = recruitmentCvTechSourcingStore.recruitmentCvTechSourcings[lastPageUploaded.value].cvTech;
            
            if (!cvTech?.id) {
                downloadNext();
                return;
            }
            
            // already cached or loading
            if (cvCache.value.find(e => e.id === cvTech.id)) {
                downloadNext();
                return;
            }
            
            // mark as loading immediately (avoid duplicate downloads)
            cvCache.value.push({
                id: cvTech.id,
                url: null,
                type: 'LOADING'
            });
            
            axios.get(`/cvTechs/download/${cvTech.id}`, {responseType: 'blob'}).then(response => {
                let url = null
                let type = 'INCOMPATIBLE'
                
                if (cvTech.type === 'PDF') {
                    url = URL.createObjectURL(new Blob([response.data], {type: 'application/pdf'}))
                    type = 'PDF'
                } else if (cvTech.type === 'IMAGE') {
                    url = URL.createObjectURL(new Blob([response.data], {type: response.data.type || 'image/*'}))
                    type = 'IMAGE'
                }
                
                // replace loading entry
                const index = cvCache.value.findIndex(e => e.id === cvTech.id);
                if (index !== -1) {
                    cvCache.value.splice(index, 1, {id: cvTech.id, url, type});
                }
            }).catch(() => {
                const index = cvCache.value.findIndex(e => e.id === cvTech.id);
                if (index !== -1) {
                    cvCache.value.splice(index, 1, {
                        ...cvCache.value[index],
                        type: 'ERROR'
                    });
                }
            }).finally(() => {
                downloadNext();
            });
            
        }
        
        function downloadNext() {
            if (lastPageUploaded.value < size.value) {
                lastPageUploaded.value++;
                downloadAndCacheCv();
            }
        }
        
        const clearCvCache = () => {
            cvCache.value.forEach(e => {
                if (e.url) URL.revokeObjectURL(e.url);
            });
            cvCache.value = [];
        };
        
        const cvTechSourcing = computed(() => {
            let result = {
                recommendSourcings: [],
                cvTech: {}
            }
            if (size.value > 0) {
                result = recruitmentCvTechSourcingStore.recruitmentCvTechSourcings[stepper.value - 1]
            }
            return result;
        });
        
        const size = computed(() => recruitmentCvTechSourcingStore.recruitmentCvTechSourcings.length);
        
        const getCachedCv = computed(() => cvCache.value.find(e => e.id === cvTechSourcing.value.cvTech?.id) || {});
        
        function findCvByState() {
            loading.value = true;
            recruitmentCvTechSourcingStore.setRecruitmentCvTechSourcings([]);
            axios.get('/cvTechSourcings', {
                params: transformFilterValues({
                    sourcingId: $route.params.id,
                    state: selectOption.value,
                    allRecords: true,
                })
            }).then(response => {
                recruitmentCvTechSourcingStore.setRecruitmentCvTechSourcings(response.data.elements);
                lastPageUploaded.value = 0;
                if (size.value > 0)
                    downloadAndCacheCv();
            }).finally(() => {
                loading.value = false;
            });
        }
        
        watch(() => [selectOption.value, refresh.value], () => {
            stepper.value = 1;
            findCvByState();
        }, {immediate: true});
        
        const cvTechSourcingStateCount = ref([]);
        const loadingCount = ref(false);
        
        function getCountByState() {
            loadingCount.value = true;
            axios.get(`/cvTechSourcings/getCountByState/${$route.params.id}`).then(response => {
                cvTechSourcingStateCount.value = response.data;
            }).finally(() => {
                loadingCount.value = false;
            });
        }
        
        function upload(items) {
            items.forEach(element => {
                recruitmentCvTechSourcingStore.pushRecruitmentCvTechSourcing(element);
            });
            downloadAndCacheCv();
        }
        
        function findById(id) {
            axios.get(`/recruitmentSourcings/${id}`).then(response => {
                sourcing.value = response.data;
            });
        }
        
        const recommendSourcings = ref([]);
        const recommendDialog = ref(false);
        
        function findInProgress() {
            axios.get('/recruitmentSourcings', {
                params: {
                    allRecords: true,
                    state: 'VALIDATION',
                }
            }).then(response => {
                recommendSourcings.value = response.data.elements;
            });
        }
        
        const saveLoading = ref(false);
        const formRefs = ref({});
        
        function save(option) {
            //if (formRefs.value[stepper.value]?.validate()) {
            saveLoading.value = true;
            axios.post('/cvTechSourcings', cvTechSourcing.value).then(response => {
                recruitmentCvTechSourcingStore.updateRecruitmentCvTechSourcing(response.data);
                if (option === 'NEXT_STEP') {
                    if (stepper.value < size.value)
                        stepper.value++;
                } else if (option === 'PREVIOUS_STEP') {
                    if (stepper.value > 1)
                        stepper.value--;
                } else if (option === 'CLOSE') {
                    $router.push({name: 'recruitment_sourcings'});
                }
                panelModel.value = 0;
                $refs.dejaVue[stepper.value - 1].check();
            }).finally(() => {
                saveLoading.value = false;
            });
            /*} else if (option === 'CLOSE') {
                $router.push({name: 'recruitment_sourcings'});
            }*/
        }
        
        const buildLoading = ref(false);
        
        function build() {
            buildLoading.value = true;
            axios.put(`/cvTechSourcings/build/${cvTechSourcing.value.id}`).then(response => {
                recruitmentCvTechSourcingStore.updateRecruitmentCvTechSourcing(response.data);
            }).finally(() => {
                buildLoading.value = false;
            });
        }
        
        onMounted(() => {
            findById($route.params.id);
            findInProgress();
        });
        
        onUnmounted(() => {
            clearCvCache();
        });
        
        return {
            recruitmentCvTechSourcingStore,
            securityStore,
            recommendDialog,
            selectOption,
            mxRules,
            readonly,
            dialog,
            panelModel,
            secondDialog,
            loading,
            saveLoading,
            buildLoading,
            inputColor,
            backgroundColor,
            chipElevation,
            cvTechSourcingStateCount,
            cvTechSourcingStates,
            cvTechSourcing,
            stepper,
            sourcing,
            recommendSourcings,
            loadingCount,
            cvCache,
            size,
            getCachedCv,
            formRefs,
            openLoading,
            downloadLoading,
            getBack,
            upload,
            downloadOrOpen,
            getCountByState,
            save,
            build,
        }
    },
}
</script>

<template>
    <div>
        <w-app
            :title="`${$t('module.recruitment_sourcing')} ${sourcing.requestPostNumber} ${$t('recruitment_request.job')} ${sourcing.requestOccupationName}`"
            show-total
            :total="size > 0 ? `${stepper}/${size}` : '0/0'"
        >
            <template v-slot:leftToolbar>
                <v-menu offset-y>
                    <template v-slot:activator="{ on }">
                        <v-btn
                            depressed
                            class="white--text"
                            icon
                            color="blue"
                            v-on="on"
                        >
                            <v-icon>mdi-dots-vertical</v-icon>
                        </v-btn>
                    </template>
                    
                    <v-list nav dense>
                        <v-list-item
                            @click="$router.push({name: 'recruitment_sourcings.filter', params: { id: sourcing.requestId }})"
                            :disabled="sourcing.closed"
                        >
                            <v-list-item-icon>
                                <v-icon color="purple">mdi-filter</v-icon>
                            </v-list-item-icon>
                            <v-list-item-title>{{ $t("common.to_filter") }}</v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>
                <v-menu offset-y>
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn
                            depressed
                            color="blue"
                            class="white--text mr-2"
                            small
                            :loading="loadingCount"
                            @click="getCountByState"
                            v-bind="attrs"
                            v-on="on"
                        >
                            {{ $t(`cv_tech_sourcing.${selectOption}`) }}
                            <v-icon right>mdi-filter</v-icon>
                        </v-btn>
                    </template>
                    <v-list dense>
                        <template v-for="(item, index) in cvTechSourcingStateCount">
                            <v-list-item
                                :key="`state-item-${index}`"
                                @click="() => {
                                    selectOption = item.state;
                                    stepper = 1;
                                }"
                            >
                                <v-list-item-title>
                                    {{ $t(`cv_tech_sourcing.${item.state}`) }}
                                </v-list-item-title>
                                <v-list-item-action>
                                    <v-list-item-action-text>
                                        <v-chip
                                            small
                                            color="blue darken-2"
                                            outlined
                                        >
                                            <strong>{{ item.total }}</strong>
                                        </v-chip>
                                    </v-list-item-action-text>
                                </v-list-item-action>
                            </v-list-item>
                            <v-divider
                                :key="`state-div-${index}`"
                                v-if="index < cvTechSourcingStateCount.length - 1"
                            />
                        </template>
                    
                    </v-list>
                </v-menu>
            </template>
            
            <template v-slot:rightToolbar>
                <str-cv-email-import
                    class="mr-2 ml-2"
                    :disabled="sourcing.closed"
                    @upload="upload"
                />
                <str-cv-folder-import
                    class="mr-2 ml-2"
                    :disabled="sourcing.closed"
                    @upload="upload"
                />
                <str-cv-tech-import
                    class="mr-2 ml-2"
                    :disabled="sourcing.closed"
                    @upload="upload"
                />
            </template>
            <template v-slot:content>
                <v-container fluid>
                    <v-row dense class="d-flex  justify-end">
                        <v-col cols="auto">
                            <w-enum-select
                                v-model="cvTechSourcing.state"
                                :items="cvTechSourcingStates"
                                :label="`${$t('recruitment_request.sourcing_action')} *`"
                                not-clearable
                                :disabled="size === 0"
                                @change="save('NEXT_STEP')"
                            />
                        </v-col>
                        <v-col cols="auto">
                            <w-btn
                                color="blue darken-2"
                                icon="mdi-page-previous"
                                :text="$t('common.back')"
                                class="mt-1"
                                :disabled="stepper === 1"
                                @press="stepper--"
                            />
                        </v-col>
                        <v-col cols="auto">
                            <w-btn
                                color="blue darken-2"
                                icon="mdi-page-next"
                                :text="$t('common.next')"
                                class="mt-1"
                                :disabled="size === 0 || stepper === size"
                                @press="stepper++"
                            />
                        </v-col>
                    
                    </v-row>
                </v-container>
                <v-skeleton-loader
                    v-if="loading"
                    ref="skeleton"
                    :type="'table'"
                    class="mx-auto"
                />
                <v-data-iterator
                    v-else
                    :items="recruitmentCvTechSourcingStore.recruitmentCvTechSourcings"
                    :items-per-page="1"
                    :page.sync="stepper"
                    hide-default-footer
                >
                    <template v-slot:default="props">
                        <v-row
                            v-for="item in props.items"
                            :key="item.id"
                        >
                            <v-col cols="3">
                                <v-form :ref="el => formRefs[stepper] = el" lazy-validation>
                                    <v-expansion-panels
                                        v-model="panelModel"
                                        dense
                                        focusable
                                    >
                                        <v-expansion-panel>
                                            <v-expansion-panel-header>
                                                <strong>{{ $t('common.details') }}</strong>
                                            </v-expansion-panel-header>
                                            <v-expansion-panel-content class="mr-n5 ml-n5">
                                                <v-container fluid>
                                                    <v-row dense>
                                                        <v-col cols="12">
                                                            <DejaVue
                                                                ref="dejaVue"
                                                                view-action
                                                                :cv-tech-sourcing-id="item.id"
                                                            />
                                                        </v-col>
                                                        <v-col cols="12">
                                                            <w-text-field
                                                                v-model="item.cvTech.lastName"
                                                                :label="$t('common.last_name')"
                                                                :rules="sourcing.requestCheckLastNameCompleted ? [mxRules.required] : []"
                                                            />
                                                        </v-col>
                                                        <v-col cols="12">
                                                            <w-text-field
                                                                v-model="item.cvTech.firstName"
                                                                :label="$t('common.first_name')"
                                                                :rules="sourcing.requestCheckFirstNameCompleted ? [mxRules.required] : []"
                                                            />
                                                        </v-col>
                                                        <v-col cols="12">
                                                            <w-date-picker
                                                                v-model="item.cvTech.birthDate"
                                                                :label="$t('common.birth_day')"
                                                                :rules="sourcing.requestCheckBirthDateCompleted ? [mxRules.required] : []"
                                                            />
                                                        </v-col>
                                                        <v-col cols="12">
                                                            <w-text-field
                                                                v-model="item.cvTech.age"
                                                                :label="$t('common.age')"
                                                                type="number"
                                                                :rules="sourcing.requestCheckAgeCompleted ? [mxRules.required] : []"
                                                            />
                                                        </v-col>
                                                        <v-col cols="12">
                                                            <w-text-field
                                                                v-model="item.cvTech.address"
                                                                :label="$t('common.address')"
                                                                :rules="sourcing.requestCheckAddressCompleted ? [mxRules.required] : []"
                                                            />
                                                        </v-col>
                                                        <v-col cols="12">
                                                            <w-text-field
                                                                v-model="item.cvTech.phone"
                                                                :label="$t('common.phone')"
                                                                :rules="sourcing.requestCheckPhoneCompleted ? [mxRules.required] : []"
                                                            />
                                                        </v-col>
                                                        <v-col cols="12">
                                                            <w-text-field
                                                                v-model="item.cvTech.email"
                                                                :label="$t('common.email')"
                                                                :rules="sourcing.requestCheckEmailCompleted ? [mxRules.required] : []"
                                                            />
                                                        </v-col>
                                                        <v-col cols="12">
                                                            <w-country-state-autocomplete
                                                                v-model="item.cvTech.stateId"
                                                                :label="$t('common.country_state')"
                                                                :rules="sourcing.requestCheckWilayaCompleted ? [mxRules.required] : []"
                                                            />
                                                        </v-col>
                                                        <!--<v-col cols="12">
                                                            <w-text-field
                                                                v-model="cvTechSourcing.cvTech.specialization"
                                                                label="Titre d'emploi"
                                                            />
                                                        </v-col>-->
                                                        <v-col cols="12">
                                                            <w-server-autocomplete
                                                                v-model="item.cvTech.specialityId"
                                                                api="dataSelects/type/RECRUITMENT_CV_TECH_SPECIALITY"
                                                                :label="$t('data_select.RECRUITMENT_CV_TECH_SPECIALITY')"
                                                                :rules="sourcing.requestCheckSpecialityCompleted ? [mxRules.required] : []"
                                                            />
                                                        </v-col>
                                                        <v-col cols="12">
                                                            <w-text-field
                                                                v-model="item.cvTech.yearOfExperience"
                                                                :label="$t('recruitment_request.experience')"
                                                                type="number"
                                                                :rules="sourcing.requestCheckLastNameCompleted ? [mxRules.required] : []"
                                                            />
                                                        </v-col>
                                                        <v-col cols="6">
                                                            <w-btn
                                                                color="teal"
                                                                icon="mdi-database-sync"
                                                                :text="$t('common.apply')"
                                                                class="ml-n1"
                                                                :loading="saveLoading"
                                                                block
                                                                :disabled="sourcing.closed"
                                                                @press="save"
                                                            />
                                                        </v-col>
                                                        <v-col cols="6">
                                                            <w-btn
                                                                color="deep-orange"
                                                                icon="mdi-head-snowflake"
                                                                text="AI"
                                                                class="ml-n1"
                                                                :loading="buildLoading"
                                                                block
                                                                outlined
                                                                :disabled="sourcing.closed"
                                                                @press="build"
                                                            />
                                                        </v-col>
                                                    </v-row>
                                                </v-container>
                                            </v-expansion-panel-content>
                                        </v-expansion-panel>
                                        <v-expansion-panel>
                                            <v-expansion-panel-header>
                                                <strong>{{ $t('recruitment_request.summary') }}</strong>
                                            </v-expansion-panel-header>
                                            <v-expansion-panel-content class="mr-n5 ml-n5">
                                                <v-container fluid>
                                                    {{ item.cvTech.summary }}
                                                </v-container>
                                            </v-expansion-panel-content>
                                        </v-expansion-panel>
                                        <v-expansion-panel>
                                            <v-expansion-panel-header>
                                                <strong>{{ $t('recruitment_request.key_words') }}</strong>
                                            </v-expansion-panel-header>
                                            <v-expansion-panel-content class="mr-n5 ml-n5">
                                                <v-container fluid>
                                                    <v-chip-group column>
                                                        <v-chip
                                                            v-for="(keyWord, index) in item.cvTech.keyWords"
                                                            :key="`keyWord-${item.cvTech.id}-${index}`"
                                                            small
                                                            label
                                                        >
                                                            <strong>{{ keyWord }}</strong>
                                                        </v-chip>
                                                    </v-chip-group>
                                                </v-container>
                                            </v-expansion-panel-content>
                                        </v-expansion-panel>
                                        <v-expansion-panel>
                                            <v-expansion-panel-header>
                                                <strong>{{ $t('common.other') }}</strong>
                                            </v-expansion-panel-header>
                                            <v-expansion-panel-content class="mr-n5 ml-n5">
                                                <v-container fluid>
                                                    <v-row dense>
                                                        <v-col cols="12">
                                                            <v-radio-group
                                                                v-model="item.cvTech.sex"
                                                                row
                                                                class="mb-n7"
                                                            >
                                                                <v-radio value="M" :label="$t('common.man')"/>
                                                                <v-radio value="F" :label="$t('common.woman')"/>
                                                            </v-radio-group>
                                                        </v-col>
                                                        <v-col cols="12">
                                                            <v-checkbox
                                                                v-model="item.cvTech.married"
                                                                :label="$t('recruitment_request.married')"
                                                                hide-details
                                                            />
                                                        </v-col>
                                                        <v-col cols="12">
                                                            <v-checkbox
                                                                v-model="item.cvTech.hasChildren"
                                                                 :label="$t('recruitment_request.has_children')"
                                                                hide-details
                                                            />
                                                        </v-col>
                                                        <v-col cols="12">
                                                            <v-checkbox
                                                                v-model="item.cvTech.hasDriverLicense"
                                                                 :label="$t('recruitment_request.has_driver_license')"
                                                                hide-details
                                                            />
                                                        </v-col>
                                                        <v-col cols="12">
                                                            <v-checkbox
                                                                v-model="item.cvTech.hasValidPassport"
                                                               :label="$t('recruitment_request.has_passport')"
                                                                hide-details
                                                            />
                                                        </v-col>
                                                        <v-col cols="12">
                                                            <v-checkbox
                                                                v-model="item.cvTech.hasCar"
                                                                 :label="$t('recruitment_request.has_vehicle')"
                                                                hide-details
                                                            />
                                                        </v-col>
                                                        <v-col cols="12">
                                                            <v-checkbox
                                                                v-model="item.cvTech.freeMilitaryService"
                                                                :label="$t('recruitment_request.free_military_service')"
                                                                hide-details
                                                            />
                                                        </v-col>
                                                    </v-row>
                                                </v-container>
                                            </v-expansion-panel-content>
                                        </v-expansion-panel>
                                        <!--<v-expansion-panel>
                                            <v-expansion-panel-header>
                                                <strong>Etats</strong>
                                            </v-expansion-panel-header>
                                            <v-expansion-panel-content class="mr-n5 ml-n5">
                                                <v-container fluid>
                                                    <v-chip-group column>
                                                        <v-chip>OCR {{ $t(`common.ACTION_STATE_${cvTechSourcing.cvTech.ocrAction}`) }}</v-chip>
                                                        <v-chip>FTP {{ $t(`common.ACTION_STATE_${cvTechSourcing.cvTech.ftpAction}`) }}</v-chip>
                                                        <v-chip>AI {{ $t(`common.ACTION_STATE_${cvTechSourcing.cvTech.aiAction}`) }}</v-chip>
                                                        <v-chip>INDEXING {{ $t(`common.ACTION_STATE_${cvTechSourcing.cvTech.indexingAction}`) }}</v-chip>
                                                    </v-chip-group>
                                                </v-container>
                                            </v-expansion-panel-content>
                                        </v-expansion-panel>-->
                                    </v-expansion-panels>
                                </v-form>
                            </v-col>
                            <v-col cols="9">
                                <v-row class="justify-center align-center fill-height">
                                    <v-col
                                        v-if="getCachedCv.type === 'LOADING'"
                                        cols="5"
                                    >
                                        <v-progress-linear color="teal" indeterminate height="10"/>
                                    </v-col>
                                    <v-col
                                        v-else-if="getCachedCv.type !== 'LOADING'"
                                        cols="5"
                                    >
                                        <h4 v-if="getCachedCv.type === 'INCOMPATIBLE'">
                                            {{ $t('recruitment_request.incompatible_view_file_description') }}
                                        </h4>
                                        <v-row dense v-if="getCachedCv.type !== 'PDF'">
                                            <v-col cols="auto">
                                                <w-btn
                                                    icon="mdi-file-download"
                                                    color="blue"
                                                    block
                                                    :loading="downloadLoading"
                                                    :text="$t('common.download')"
                                                    @press="downloadOrOpen('cvTechs', item.cvTech.id, item.cvTech.fileFullName, false)"
                                                />
                                            </v-col>
                                            <v-col cols="auto">
                                                <w-btn
                                                    icon="mdi-file-find"
                                                    color="blue"
                                                    :text="$t('common.to_open')"
                                                    :loading="openLoading"
                                                    block
                                                    @press="downloadOrOpen('cvTechs', item.cvTech.id, item.cvTech.fileFullName, true)"
                                                />
                                            </v-col>
                                        </v-row>
                                    </v-col>
                                    
                                    <v-col
                                        v-if="getCachedCv.type === 'PDF'"
                                        cols="12"
                                    >
                                        <iframe
                                            :src="`${getCachedCv.url}#zoom=page-fit`"
                                            type="application/pdf"
                                            width="100%"
                                            height="750px"
                                            ref="pdfIframe"
                                        />
                                    </v-col>
                                    <v-col
                                        v-else-if="getCachedCv.type === 'IMAGE'"
                                        cols="12"
                                    >
                                        <v-img :src="getCachedCv.url"/>
                                    </v-col>
                                </v-row>
                            </v-col>
                        </v-row>
                    </template>
                </v-data-iterator>
                
                <div
                    v-if="size === 0"
                    class="d-flex justify-center align-center"
                    style="height: 100vh;"
                >
                    <v-img
                        max-width="600"
                        contain
                        :src="`${securityStore.getImageLink()}/no-data.jpg`"
                        alt="no-data"/>
                </div>
            </template>
        </w-app>
        <router-view/>
    </div>
</template>